import './App.css';

import * as Sentry from '@sentry/react';
import { QueryClient } from '@tanstack/react-query';
import React, { Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import {
  createBrowserRouter,
  LoaderFunction,
  LoaderFunctionArgs,
  matchPath,
  Navigate,
  NavigateProps,
  Outlet,
  redirect,
  RouteObject,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';

import { api } from './api/backend';
import {
  agentLoggedInSelector,
  AuthState,
  isLoggedInSelector,
  useAgentLoggedIn,
  useAuthStore,
  useIsLoggedIn,
} from './api/login';
import { useCutlistState } from './api/store';
import { CutlistAgentHeader } from './blocks/CutlistAgentHeader';
import CutrLabel from './blocks/CutrLabel';
import { DemoFeaturesSidebar } from './blocks/DemoFeaturesSidebar/DemoFeaturesSidebar';
import Header from './blocks/Header';
import {
  useApplyTheme,
  useInitCutlist,
  useLoginByKeyParam,
  useLoginByTokenParam,
  usePageViews,
  useThirdPartyLogin,
  useTogglePricing,
} from './hooks';
import AccountDetails from './pages/AccountDetails';
import { AgentEditOrderPage } from './pages/AgentEditOrderPage';
import { AgentLogin } from './pages/AgentLogin';
import { AgentOrdersPage } from './pages/AgentOrdersPage';
import { AgentQuoteCheckout } from './pages/AgentQuoteCheckout';
import { AgentQuoteFlow } from './pages/AgentQuoteFlow';
import { AgentQuoteParts } from './pages/AgentQuoteParts';
import { AgentQuotePrice } from './pages/AgentQuotePrice';
import Cutlist from './pages/Cutlist';
import { CutlistRoutes } from './pages/CutlistRoutes';
import { CutlistSettings } from './pages/CutlistSettings';
import Done from './pages/Done';
import { NotFound } from './pages/Errors';
import LandingPage from './pages/LandingPage';
import { OrderPage, SkeletonOrderPage } from './pages/Order';
import OrderReview from './pages/OrderReview';
import { Orders } from './pages/Orders';
import { Production } from './pages/Production';
import { RouterButton } from './primitives/Button';
import { Illustration } from './primitives/Icons';
import { ProductFruitsProvider } from './ProductFruitsProvider';
import { useCreateCutlist } from './queries/crud';
import {
  cutlistEmailLoader,
  cutlistLoader,
  priceLoader,
} from './queries/loaders';
import {
  starterCutlistLoader,
  starterDetailsLoader,
  starterIndexLoader,
  starterReviewLoader,
} from './starter-tier/loaders';
import { StarterDetails } from './starter-tier/pages/StarterDetails';
import { StarterDone } from './starter-tier/pages/StarterDone';
import { StarterParts } from './starter-tier/pages/StarterParts';
import { StarterReview } from './starter-tier/pages/StarterReview';
import { StarterLayout } from './starter-tier/StarterLayout';
import { Configurator } from './stories/layout/Configurator';
import { CONFIGURATOR_ROUTE } from './stories/layout/types';
import {
  getSourceConfig,
  isStarterTier,
  useCurrentFeatures,
  useTicker,
} from './theme';
import {
  cleanupLocalStorage,
  iframePostMessage,
  isInIframe,
  usePostMessageResponder,
} from './utils/misc';
import { useLocationPageTitle } from './utils/page-title';
import { WindowsAgentHeader } from './windows/blocks/WindowsAgentHeader';
import { PositionConfiguration } from './windows/interfaces/configurations/PositionConfiguration';
import { AgentOrderReview } from './windows/pages/AgentOrderReview';
import { AgentWorkspace } from './windows/pages/AgentWorkspace';
import { NotFound as WindowsNotFound } from './windows/pages/Errors';
import { WindowsPlayground } from './windows/pages/Playground';
import { ResellerLogin } from './windows/pages/ResellerLogin';
import { ResellerOrder } from './windows/pages/ResellerOrder';
import { ResellerOrderCheckout } from './windows/pages/ResellerOrderCheckout';
import { ResellerOrderConfiguration } from './windows/pages/ResellerOrderConfiguration';
import { ResellerOrderIndexRedirect } from './windows/pages/ResellerOrderIndexRedirect';
import { ResellerOrderReview } from './windows/pages/ResellerOrderReview';
import { ResellerSettings } from './windows/pages/ResellerSettings';
import { ResellerWorkspace } from './windows/pages/ResellerWorkspace';
import {
  orderLoader as windowOrderLoader,
  windowSettingsLoader,
} from './windows/queries/order/loaders';
import { windowPositionLoader } from './windows/queries/position/loaders';
import ResellerLayout from './windows/ResellerLayout';

const PD = React.lazy(() => import('./pages/Playground'));
const DevPlayground = () => (
  <React.Suspense fallback={null}>
    <PD />
  </React.Suspense>
);

const CutlistConfigurator = () => (
  <React.Suspense fallback={null}>
    <Configurator />
  </React.Suspense>
);

const NoDisabledRoutesForSource = () => {
  const { allowedRoutes } = getSourceConfig();
  const ticker = useTicker();

  const location = useLocation();
  if (allowedRoutes) {
    const match = allowedRoutes.some((pattern) =>
      matchPath(pattern, location.pathname)
    );
    if (!match) {
      console.error('Route is not allowed for source', { location });
      Sentry.captureMessage('Route is not allowed for source', {
        level: 'error',
        extra: { source: ticker, pathname: location.pathname, allowedRoutes },
      });
      return <NoMatch />;
    }
  }
  return <Outlet />;
};

const OpenThirdPartyCutlist = () => {
  const { error } = useThirdPartyLogin();
  if (error) {
    return <NotFound />;
  }
  return null;
};

const LoginByToken = () => {
  useLoginByTokenParam();
  return null;
};

const LoginByKey = () => {
  useLoginByKeyParam();
  return null;
};

const StartNewCutlist = () => {
  const logout = useAuthStore((state) => state.logout);
  const { mutateAsync: createCutlist } = useCreateCutlist();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const started = React.useRef(false);

  React.useEffect(() => {
    if (started.current) return;
    started.current = true;
    logout();
    createCutlist()
      .then((cutlist) =>
        navigate(`/cutlist/${cutlist.id}/parts?${searchParams.toString()}`, {
          replace: true,
        })
      )
      .catch(() => navigate('/home', { replace: true }));
  }, [logout, createCutlist, navigate, searchParams]);

  return null;
};

const STARTER_PATHS = ['/parts', '/details', '/review', '/done'];

const PrivateRoutes = () => {
  const { allowUnauthUsers } = useCurrentFeatures();
  const isLoggedIn = useIsLoggedIn();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const pathname = location.pathname;

  if (allowUnauthUsers && pathname.includes('/parts')) {
    return <Outlet />;
  }

  if (isStarterTier() && STARTER_PATHS.some((p) => pathname.endsWith(p))) {
    return <Outlet />;
  }

  if (!isLoggedIn) {
    const redirect = searchParams.get('redirect');
    if (!redirect && !['/', '/home', '/login'].includes(pathname)) {
      const redirectUrl = encodeURIComponent(pathname);
      searchParams.set('redirect', redirectUrl);
    }
    const path = `/login?${searchParams.toString()}`;
    return <Navigate to={path} />;
  }

  return <Outlet />;
};

const AgentRoutes = () => {
  const isAgentLoggedIn = useAgentLoggedIn();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const pathname = location.pathname;

  if (!isAgentLoggedIn) {
    const redirect = searchParams.get('redirect');
    if (!redirect) {
      const redirectUrl = encodeURIComponent(pathname);
      searchParams.set('redirect', redirectUrl);
    }
    const path = `/admin?${searchParams.toString()}`;
    return <Navigate to={path} />;
  }

  return <Outlet />;
};

const ResellerRoutes = () => {
  const isResellerLoggedIn = useIsLoggedIn();
  const [searchParams] = useSearchParams();
  const pathname = useLocation().pathname;
  if (!isResellerLoggedIn || searchParams.get('token')) {
    if (
      !searchParams.get('redirect') &&
      !['/', '/home', '/login'].includes(pathname)
    ) {
      const redirectUrl = encodeURIComponent(pathname);
      searchParams.set('redirect', redirectUrl);
    }
    const path = `/windows/login?${searchParams.toString()}`;
    return <Navigate to={path} replace />;
  }

  return <Outlet />;
};

const SessionRoutes = () => {
  const { login } = useAuthStore();
  const [configuratorToken, setConfiguratorToken] = React.useState<
    string | null
  >();

  React.useEffect(() => {
    const handleLoginBySession = async () => {
      const data = await api.loginBySession();
      const { email, clientNumber, token, userLeadId } = data;
      login({ email, clientNumber, token, userLeadId });
      setConfiguratorToken(token);
    };

    handleLoginBySession();
  }, [login]);

  return configuratorToken ? <Outlet /> : <NotFound />;
};

const App = () => {
  useInitCutlist();
  useTogglePricing();
  useApplyTheme();
  usePageViews();
  useLocationPageTitle();

  return (
    <>
      <Outlet />
      <Footer />
      <ProductFruitsProvider />
      <DemoFeaturesSidebar />
    </>
  );
};

export const router = (queryClient: QueryClient) =>
  createBrowserRouter([
    {
      element: <App />,
      children: [
        { path: '/cutlist/:id/production', element: <Production /> },
        {
          element: <NoDisabledRoutesForSource />,
          children: [
            {
              element: <Layout />,
              path: '/',
              children: [
                {
                  element: <PrivateRoutes />,
                  children: [
                    isStarterTier()
                      ? {
                          path: 'cutlist/:id',
                          element: <StarterLayout />,
                          loader: starterCutlistLoader(queryClient),
                          hydrateFallbackElement: <SkeletonOrderPage />,
                          errorElement: <NotFound />,
                          children: [
                            { path: 'parts', element: <StarterParts /> },
                            {
                              path: 'details',
                              element: <StarterDetails />,
                              loader: starterDetailsLoader(queryClient),
                            },
                            {
                              path: 'review',
                              element: <StarterReview />,
                              loader: starterReviewLoader(queryClient),
                            },
                            {
                              index: true,
                              element: <Cutlist />,
                              loader: starterIndexLoader(queryClient),
                            },
                          ],
                        }
                      : {
                          element: <CutlistRoutes />,
                          path: 'cutlist/:id',
                          children: [
                            {
                              path: 'parts',
                              element: <OrderPage />,
                            },
                            { path: 'details', element: <AccountDetails /> },
                            { path: 'review', element: <OrderReview /> },
                            { path: 'done', element: <Done /> },
                            { index: true, element: <Cutlist /> },
                          ],
                        },
                    ...(isStarterTier()
                      ? [{ path: 'cutlist/:id/done', element: <StarterDone /> }]
                      : []),
                    {
                      element: <Orders />,
                      index: true,
                    },
                  ],
                },
                { path: 'home', element: <LandingPage /> },
                { path: 'login', element: <LandingPage /> },
                { path: 'new', element: <LoginByKey /> },
                ...(isStarterTier()
                  ? [{ path: 'start', element: <StartNewCutlist /> }]
                  : []),
                { path: 'token', element: <LoginByToken /> },
                { path: 'open', element: <OpenThirdPartyCutlist /> },
                { path: 'redirect', element: <Redirect /> },
                import.meta.env.VITE_CUTR_ENV === 'development'
                  ? { path: 'playground', element: <DevPlayground /> }
                  : { path: '*', element: <NoMatch /> },
                { path: '*', element: <NoMatch /> },
              ],
            },
            {
              element: <AgentLayout />,
              path: '/admin',
              children: wrapLoadersWithAuth(isAgentLoggedIn, [
                { element: <AgentLogin />, index: true },
                {
                  element: <AgentRoutes />,
                  children: [
                    {
                      element: <Outlet />,
                      path: 'cutlists',
                      children: [
                        {
                          element: <AgentOrdersPage />,
                          path: 'orders',
                        },
                        {
                          element: <CutlistSettings />,
                          path: 'settings',
                        },
                        {
                          element: <AgentEditOrderPage />,
                          path: 'orders/:id/edit',
                        },
                        {
                          element: <AgentQuoteFlow />,
                          path: 'orders/:id/quote',
                          loader: cutlistLoader(queryClient),
                          errorElement: <NotFound redirectTo="/admin" />,
                          children: [
                            {
                              path: 'parts',
                              loader: cutlistEmailLoader(queryClient),
                              element: <AgentQuoteParts />,
                            },
                            {
                              path: 'price',
                              loader: priceLoader(queryClient),
                              element: <AgentQuotePrice />,
                            },
                            {
                              path: 'checkout',
                              loader: priceLoader(queryClient),
                              element: <AgentQuoteCheckout />,
                            },
                          ],
                        },
                      ],
                    },
                    {
                      element: <Outlet />,
                      path: 'windows',
                      errorElement: (
                        <WindowsNotFound redirectTo="/admin/windows/orders" />
                      ),
                      children: [
                        {
                          element: <AgentWorkspace />,
                          path: 'orders',
                        },
                        {
                          element: <AgentOrderReview />,
                          path: 'orders/:orderId',
                          id: 'agent/windows/orders/:orderId',
                          loader: windowOrderLoader(queryClient),
                        },
                      ],
                    },
                  ],
                },
                { path: '*', element: <NoMatch /> },
              ]),
            },
            {
              element: <ResellerLayout />,
              path: '/windows',
              children: [
                ...(import.meta.env.VITE_CUTR_ENV === 'development'
                  ? [
                      {
                        element: <WindowsPlayground />,
                        path: 'playground',
                      },
                    ]
                  : []),
                { element: <ResellerLogin />, path: 'login' },

                {
                  element: <ResellerRoutes />,
                  loader: requireAuthRedirect('/windows/login'),
                  children: wrapLoadersWithAuth(isLoggedIn, [
                    {
                      element: <NavigateWithParams to="workspace" replace />,
                      index: true,
                    },
                    {
                      element: <ResellerWorkspace />,
                      path: 'workspace',
                    },
                    {
                      loader: windowSettingsLoader,
                      element: <ResellerSettings />,
                      path: 'settings',
                    },
                    {
                      element: <ResellerOrder />,
                      path: 'orders/:orderId',
                      errorElement: (
                        <WindowsNotFound redirectTo="/windows/workspace" />
                      ),
                      id: 'windows/orders/:orderId',
                      loader: windowOrderLoader(queryClient),
                      children: [
                        {
                          index: true,
                          element: <ResellerOrderIndexRedirect />,
                        },
                        {
                          path: 'configuration',
                          element: <ResellerOrderConfiguration />,
                          children: [
                            {
                              path: ':positionId',
                              loader: windowPositionLoader(queryClient),
                              element: <PositionConfiguration />,
                            },
                          ],
                        },
                        {
                          path: 'review',
                          element: <ResellerOrderReview />,
                        },
                        {
                          path: 'checkout',
                          element: <ResellerOrderCheckout />,
                        },
                      ],
                    },
                  ]),
                },

                {
                  path: '*',
                  element: <WindowsNotFound redirectTo="/windows/workspace" />,
                },
              ],
            },
          ],
        },
        import.meta.env.VITE_CUTR_ENV !== 'production'
          ? {
              element: <SessionRoutes />,
              children: [
                {
                  element: <CutlistConfigurator />,
                  path: `/${CONFIGURATOR_ROUTE}`,
                  children: [
                    {
                      path: `/${CONFIGURATOR_ROUTE}/:id`,
                      element: <CutlistConfigurator />,
                    },
                  ],
                },
              ],
            }
          : { path: '*', element: <NoMatch /> },
      ],
    },
  ]);

function isLoggedIn() {
  const auth = localStorage.getItem('cutlist-auth');
  if (!auth) {
    return false;
  }

  try {
    const authState = JSON.parse(auth).state as AuthState;
    return isLoggedInSelector(authState);
  } catch (error) {
    return false;
  }
}
function isAgentLoggedIn() {
  const auth = localStorage.getItem('cutlist-auth');
  if (!auth) {
    return false;
  }

  try {
    const authState = JSON.parse(auth).state as AuthState;
    return agentLoggedInSelector(authState);
  } catch (error) {
    return false;
  }
}

/**
 * Wrap a loader with auth check so it only executes if the user is logged in. Otherwise it will throw a 401 error.
 */
function requireAuth(
  isAuthFn: () => boolean,
  loader: (args: LoaderFunctionArgs) => ReturnType<LoaderFunction>
): (args: LoaderFunctionArgs) => ReturnType<LoaderFunction> {
  return (args: LoaderFunctionArgs): ReturnType<LoaderFunction> => {
    if (!isAuthFn()) {
      throw new Response('Unauthorized', { status: 401 });
    }
    return loader(args);
  };
}

/**
 * Wrap the load of all routes (and their children) with auth check so they only execute if the user is logged in
 */
const wrapLoadersWithAuth = <T extends RouteObject[]>(
  isAuthFn: () => boolean,
  routes: T
): T => {
  return routes.map((route) => {
    const wrappedRoute: RouteObject = { ...route };

    if (typeof wrappedRoute.loader === 'function') {
      wrappedRoute.loader = requireAuth(isAuthFn, wrappedRoute.loader);
    }

    if (wrappedRoute.children) {
      wrappedRoute.children = wrapLoadersWithAuth(
        isAuthFn,
        wrappedRoute.children
      );
    }

    return wrappedRoute;
  }) as T;
};

/**
 * Wrap a loader with auth check so it redirects to the login page if the user is not logged in or if the url contains a token parameter.
 * Otherwise it will execute the loader.
 */
function requireAuthRedirect(
  path = '/login',
  loader: LoaderFunction = () => null
): LoaderFunction {
  return (args: LoaderFunctionArgs) => {
    const url = new URL(args.request.url);

    if (isLoggedIn() && !url.searchParams.get('token')) {
      return loader(args);
    }
    const searchParams = url.searchParams;
    const pathname = url.pathname;

    const redirectUrl = encodeURIComponent(pathname);
    searchParams.set('redirect', redirectUrl);
    const redirectPath = `${path}?${searchParams.toString()}`;

    return redirect(redirectPath);
  };
}

function Layout() {
  const Wrapper = isInIframe() ? EmbeddedInIframe : React.Fragment;

  return (
    <>
      <Header />

      <Wrapper>
        <div className="contentWrapper">
          <Outlet />
        </div>
      </Wrapper>
    </>
  );
}

const Footer = () => {
  const { showCutrLabel } = getSourceConfig();

  return showCutrLabel ? (
    <footer>
      <div className="footer-content">
        <CutrLabel />
      </div>
    </footer>
  ) : null;
};

const EmbeddedInIframe = ({ children }: { children: React.ReactElement }) => {
  const { hasMaterials } = useCutlistState();

  usePostMessageResponder('cleanup', async (payload) => {
    cleanupLocalStorage(payload.storageKey);
  });

  React.useEffect(() => {
    if (!hasMaterials) return;
    iframePostMessage('loading', { isReady: true });
  }, [hasMaterials]);

  return children;
};

type AgentLayout = 'cutlists' | 'windows';

function AgentLayout() {
  const source = useTicker();

  const getLayoutKey = () => {
    if (source === 'SORPETALER') return 'windows';

    return 'cutlists';
  };

  const layoutKey = getLayoutKey();

  const headers: Record<AgentLayout, () => React.ReactElement> = {
    windows: WindowsAgentHeader,
    cutlists: CutlistAgentHeader,
  };

  const classNames: Record<AgentLayout, string> = {
    cutlists: 'cutlistAgentLayout',
    windows: 'windowsAgentLayout',
  };

  const HeaderComponent = headers[layoutKey] ?? headers.cutlists;
  const className = classNames[layoutKey] ?? classNames.cutlists;

  return (
    <>
      <HeaderComponent />

      <div className={className}>
        <Suspense fallback={'...'}>
          <Outlet />
        </Suspense>
      </div>
    </>
  );
}

function NoMatch() {
  const { t } = useTranslation();
  return (
    <div className="content" style={{ display: 'grid', placeItems: 'center' }}>
      <div style={{ maxWidth: '15rem', minWidth: '10rem' }}>
        <Illustration />
      </div>
      <h2>{t('translation.notfound.nothing')}</h2>
      <p>{t('translation.notfound.noexist')}</p>
      <p>
        <RouterButton to="/">{t('translation.done.homeCTA')}</RouterButton>
      </p>
    </div>
  );
}

function Redirect() {
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get('redirect');

  React.useEffect(() => {
    if (redirect) {
      window.location.href = redirect;
    }
  }, [redirect]);

  if (!redirect) {
    return <NotFound />;
  }

  return <></>;
}

// Generic NavigateWithParams component
function NavigateWithParams(props: NavigateProps) {
  const location = useLocation();

  // Support both string and object for 'to' prop
  let toPath = '';
  let toSearch = '';
  if (typeof props.to === 'string') {
    const [path, search] = props.to.split('?');
    toPath = path;
    toSearch = search ? `?${search}` : '';
  } else if (props.to && typeof props.to === 'object') {
    toPath = props.to.pathname || '';
    toSearch = props.to.search || '';
  }

  // Merge query params
  const toParams = new URLSearchParams(toSearch);
  const locationParams = new URLSearchParams(location.search);
  // location params take precedence
  for (const [key, value] of locationParams.entries()) {
    toParams.set(key, value);
  }
  const mergedSearch = toParams.toString();
  const mergedTo = mergedSearch ? `${toPath}?${mergedSearch}` : toPath;

  return <Navigate {...props} to={mergedTo} />;
}
